<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { Dropdown as VDropdown } from 'floating-vue'
import { cva } from 'class-variance-authority'
import { useI18n } from 'vue-i18n'
import { useQueryClient } from '@tanstack/vue-query'
import { format, formatDistanceToNowStrict, isToday, isYesterday } from 'date-fns'
import { compact, uniq } from 'lodash'
import { SpBadge, SpButton } from '@tithely/sproutkit-vue'
import { PhChatDots, PhChats } from '@phosphor-icons/vue'
import useUtils from '@/store/utils'
import useInboxLiteAccess from '@/composables/nav/inboxLite'
import { useInboxConversations, useSetUnreadMessageCount, useUnreadMessageCount } from '@/queries/messages/inbox'
import { usePeopleByMdsIds } from '@/queries/people'
import { useHubApi } from '@/composables/tithelyHub'
import useMessagingConversationChannel from '@/composables/useMessagingConversationChannel'
import { QUERY_KEYS } from '@/constants/query'
import { buildImageUrl } from '@/utils/imageUrl'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import type {
    ConversationUpdatedEvent,
    InboxConversation,
    UnreadMessageStatusUpdateEvent
} from '@/types/composables/tithelyMessaging'

interface InboxRow {
    id: string
    /** Link to the Breeze person's Messages tab; `null` while unresolved or for contacts with no Breeze person. */
    href: string | null
    name: string
    avatarUrl: string
    unreadCount: number
    subtitle: string
    timestamp: string | null
}

const { t } = useI18n()
const utils = useUtils()
const queryClient = useQueryClient()
const { hasInboxLiteAccess } = useInboxLiteAccess()

const open = ref(false)

// Only fetch while the panel is open; the live channel below keeps it fresh.
const { data: conversations, isPending, isLoadingError, isRefetchError, refetch } = useInboxConversations(open)

// Cross-app hop: rows are Messaging-service conversations, but they link to the Breeze
// person's Messages tab and show the Breeze profile picture. Resolve every row's member
// id in one request; contacts with no Breeze person are simply absent and render static.
const mdsIds = computed(() => uniq(compact((conversations.value ?? []).map(c => c.contact_mds_id))))
const { data: peopleByMdsId } = usePeopleByMdsIds(mdsIds, open)

// The badge's count is fetched up front, since the button is always on screen,
// and the live channel below keeps it current.
const { data: unreadCount, isError: isUnreadCountError } = useUnreadMessageCount(hasInboxLiteAccess)
const setUnreadMessageCount = useSetUnreadMessageCount()

// Live refresh: the org-wide Messaging channel fires on any conversation change.
// Invalidation only refetches while the panel is open, otherwise it just marks
// the cached list stale for the next open.
const { getConnection } = useHubApi()
const messagingOrganizationId = ref<string>()
watch(
    hasInboxLiteAccess,
    async hasAccess => {
        if (hasAccess && !messagingOrganizationId.value) {
            const { organizationId } = await getConnection()
            messagingOrganizationId.value = organizationId
        }
    },
    { immediate: true }
)

const isUnreadMessageStatusUpdate = (
    event: ConversationUpdatedEvent | UnreadMessageStatusUpdateEvent
): event is UnreadMessageStatusUpdateEvent => `unread_count` in event

useMessagingConversationChannel(messagingOrganizationId, event => {
    // An unread-status event carries the org-wide count itself, so the badge takes it as-is.
    if (isUnreadMessageStatusUpdate(event)) {
        setUnreadMessageCount(event.unread_count)
    }
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.inboxConversations() })
})

// Past this the badge reads "99+"; the button's label still carries the exact count.
const UNREAD_BADGE_MAX = 99

// Hidden until the count loads, at zero, and on error: a stale or failed count
// is worse than none.
const unreadBadge = computed(() => {
    const count = unreadCount.value
    if (isUnreadCountError.value || !count) {
        return ``
    }
    return count > UNREAD_BADGE_MAX ? `${UNREAD_BADGE_MAX}+` : String(count)
})

const buttonLabel = computed(() =>
    unreadBadge.value
        ? `${t(`nav.messages`)}, ${t(`nav.inboxLite.newReplies`, unreadCount.value ?? 0)}`
        : t(`nav.messages`)
)

// "1 minute ago" / "4 hours ago" today, "Yesterday", then a short date.
const formatTimestamp = (iso: string | null): string => {
    if (!iso) {
        return ``
    }
    const date = new Date(iso)
    if (isNaN(date.getTime())) {
        return ``
    }
    if (isToday(date)) {
        return formatDistanceToNowStrict(date, { addSuffix: true })
    }
    if (isYesterday(date)) {
        return t(`nav.inboxLite.yesterday`)
    }
    return format(date, `MM/dd/yy`)
}

// The list is requested with `unread: true`, so every row has at least one unread
// inbound message. Use the service's count when it has one; the inbox list currently
// always reports `unread_count: 0` (its SQL computes the flag but never maps it), in
// which case the row is still unread and shows as one new reply.
const unreadCountFor = (conversation: InboxConversation): number => Math.max(conversation.unread_count, 1)

const toRow = (conversation: InboxConversation): InboxRow => {
    const unreadCount = unreadCountFor(conversation)
    const person = conversation.contact_mds_id ? peopleByMdsId.value?.[conversation.contact_mds_id] : undefined
    return {
        id: conversation.id,
        href: person ? `/people/view/${person.id}/message` : null,
        name: `${conversation.contact_first_name ?? ``} ${conversation.contact_last_name ?? ``}`.trim(),
        // Breeze picture once the person resolves, else the Messaging contact's, else generic.
        avatarUrl: buildImageUrl(person?.profilePicture || conversation.contact_avatar_image),
        unreadCount,
        subtitle: t(`nav.inboxLite.newReplies`, unreadCount),
        timestamp: conversation.last_received_date ?? conversation.last_activity_date
    }
}

const rows = computed(() => (conversations.value ?? []).map(toRow))

// A failed fetch must never read as "no new replies". A refetch failure keeps the
// rows it already has, but with nothing usable to show it is an error like any other.
const fetchFailed = computed(() => isLoadingError.value || (isRefetchError.value && !rows.value.length))

const mobile = computed(() => utils.getMaxWidth(`md`))

// On mobile the 16px offset drops the panel flush with the bottom of TrinityHeaderMobile.
const dropdownProps = computed(() => ({
    triggers: [`click`],
    placement: `bottom-end`,
    distance: mobile.value ? 16 : 9,
    theme: `main-nav-settings`,
    popperClass: mobile.value ? `horizon-ui inbox-lite-mobile` : `horizon-ui`,
    autoHide: true,
    strategy: `fixed`
}))

const panelClasses = cva(``, {
    variants: {
        mobile: {
            true: `w-screen`,
            false: `w-[25.75rem] max-w-[100vw]`
        }
    }
})

const buttonClasses = cva(
    `relative flex size-10 items-center justify-center rounded-full !p-0 transition-all duration-200 ease-in-out`,
    {
        variants: {
            open: {
                true: `bg-primary text-white`,
                false: `text-primary hover:bg-secondary-200`
            }
        }
    }
)

const nameClasses = `!my-0 truncate font-sans text-base/5 font-bold text-primary`

// A row is a link only once it resolves to a Breeze person; otherwise it's static.
const rowClasses = cva(`flex w-full items-center gap-3.5 !px-6 !py-3.5 text-left`, {
    variants: {
        clickable: {
            true: `no-underline transition-colors hover:bg-secondary-100 focus-visible:bg-secondary-100 focus-visible:outline-none`,
            false: ``
        }
    }
})

const timestampClasses = `shrink-0 font-sans text-xs/normal font-medium italic text-secondary-500`

const stateClasses = `flex flex-col items-center gap-3 !px-6 !py-8 text-center`
const stateTitleClasses = `!my-0 font-sans text-base/5 font-bold text-primary`
const stateBodyClasses = `!my-0 font-sans text-sm/5 text-theme-foreground`
const refreshNoticeClasses = `flex items-center justify-between gap-3 bg-warning-100 !px-6 !py-2 font-sans text-xs/5 text-theme-foreground`
</script>

<template>
    <div
        v-if="hasInboxLiteAccess"
        class="!ml-4">
        <VDropdown
            v-bind="dropdownProps"
            v-model:shown="open">
            <button
                type="button"
                :aria-label="buttonLabel"
                :title="$t(`nav.messages`)"
                :class="buttonClasses({ open })">
                <PhChats
                    class="size-7"
                    weight="duotone" />
                <SpBadge
                    v-if="unreadBadge"
                    size="sm"
                    intent="warning"
                    class="pointer-events-none absolute -right-1 -top-1"
                    aria-hidden="true">
                    {{ unreadBadge }}
                </SpBadge>
            </button>

            <template #popper>
                <div :class="panelClasses({ mobile })">
                    <header class="flex items-center gap-4 !p-6">
                        <h3 class="!my-0 font-sans text-2xl/7 font-medium text-theme-foreground">
                            {{ $t(`nav.inboxLite.title`) }}
                        </h3>

                        <SpBadge
                            size="sm"
                            intent="primary">
                            {{ $t(`nav.inboxLite.new`) }}
                        </SpBadge>
                    </header>

                    <div class="max-h-[25.5rem] overflow-y-auto border-t border-solid border-secondary-200">
                        <div
                            v-if="isPending"
                            class="flex justify-center !p-8">
                            <LoadingSpinner class="size-6 text-primary" />
                        </div>

                        <div
                            v-else-if="fetchFailed"
                            :class="stateClasses">
                            <p :class="stateBodyClasses">{{ $t(`nav.inboxLite.error`) }}</p>
                            <SpButton
                                intent="primary"
                                variant="outline"
                                size="sm"
                                @click="refetch()">
                                {{ $t(`nav.inboxLite.retry`) }}
                            </SpButton>
                        </div>

                        <div
                            v-else-if="!rows.length"
                            :class="stateClasses">
                            <span
                                class="flex size-12 items-center justify-center rounded-full bg-secondary-100"
                                aria-hidden="true">
                                <PhChatDots
                                    class="size-6 text-primary"
                                    weight="fill" />
                            </span>
                            <p :class="stateTitleClasses">{{ $t(`nav.inboxLite.empty.title`) }}</p>
                            <p :class="stateBodyClasses">{{ $t(`nav.inboxLite.empty.description`) }}</p>
                        </div>

                        <template v-else>
                            <div
                                v-if="isRefetchError"
                                role="status"
                                :class="refreshNoticeClasses">
                                <span>{{ $t(`nav.inboxLite.refreshError`) }}</span>
                                <button
                                    type="button"
                                    class="shrink-0 font-medium underline"
                                    @click="refetch()">
                                    {{ $t(`nav.inboxLite.retry`) }}
                                </button>
                            </div>

                            <ul
                                class="relative !m-0 list-none !p-0 shadow-md before:absolute before:inset-y-0 before:left-0 before:w-[5px] before:bg-primary">
                                <li
                                    v-for="row in rows"
                                    :key="row.id">
                                    <component
                                        :is="row.href ? `a` : `div`"
                                        :href="row.href ?? undefined"
                                        :class="rowClasses({ clickable: !!row.href })">
                                        <div class="relative size-[42px] shrink-0">
                                            <img
                                                :src="row.avatarUrl"
                                                alt=""
                                                class="size-full rounded-full object-cover" />
                                            <span
                                                class="absolute bottom-0 right-0 size-3 rounded-full bg-warning-600 ring-1 ring-white"
                                                aria-hidden="true" />
                                        </div>
                                        <div class="min-w-0 flex-1">
                                            <div class="flex items-start justify-between gap-2">
                                                <p :class="nameClasses">{{ row.name }}</p>
                                                <time
                                                    :datetime="row.timestamp ?? undefined"
                                                    :class="timestampClasses">
                                                    {{ formatTimestamp(row.timestamp) }}
                                                </time>
                                            </div>
                                            <p class="!my-0 truncate font-sans text-xs/5 text-theme-foreground">
                                                {{ row.subtitle }}
                                            </p>
                                        </div>
                                    </component>
                                </li>
                            </ul>
                        </template>
                    </div>
                </div>
            </template>
        </VDropdown>
    </div>
</template>

<style lang="scss">
.inbox-lite-mobile .v-popper__wrapper {
    @apply rounded-none;
}
</style>
